import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser, useAccessToken } from '@/features/auth/state/auth'

import api from '@/lib/api'

/// CREATE
export function useCreatedApuracao() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({
      body
    }: {
      body: {
        id_client: string
        id_product: string
        id_tenant: string
      }
    }) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.post(`/apuracao`, body)

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao atualizar o tipo de usuário')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] }) // Atualiza os dados do usuário após a alteração
    },
    onError: error => {
      console.error('Erro ao atualizar o tipo de usuário:', error)
    }
  })
}

export function useCreatedCredit() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({
      body
    }: {
      body: {
        id_client: string
        id_product: string
        id_apuracao: string
        id_apuracao_competencia?: string
      }
    }) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.post(`/credits`, body)

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao criar crédito')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credits'] })
    },
    onError: error => {
      console.error('Erro ao criar crédito:', error)
    }
  })
}

/// UPDATE
export function useUpdateApuracao() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({
      id,
      body
    }: {
      id: string
      body: {
        rubrica?: number
        saldo?: number
      }
    }) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.patch(`/apuracao/${id}`, body)

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao atualizar o tipo de usuário')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] }) // Atualiza os dados do usuário após a alteração
    },
    onError: error => {
      console.error('Erro ao atualizar o tipo de usuário:', error)
    }
  })
}

export function useUpdateCredit() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({
      body
    }: {
      body: {
        id: string
        anoCompetencia: string
        mesCompetencia: string
        creditOrigin: number
        creditReal: number
        compensacao: number
        comissao: number
        isPago: boolean
        idTenant: string
        comissao_porcentagem?: number
        id_apuracao_competencia?: string
      }
    }) => {
      console.log('body', body)
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.put(`/credits`, body)

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao atualizar o tipo de usuário')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] }) // Atualiza os dados do usuário após a alteração
    },
    onError: error => {
      console.error('Erro ao atualizar o tipo de usuário:', error)
    }
  })
}

export function useUpdateFunds() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({
      id_apuracao,
      verbas,
      dateStart,
      dateEnd
    }: {
      id_apuracao: string
      verbas: any[]
      dateStart: string
      dateEnd: string
    }) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.put(`/funds/${id_apuracao}/${dateStart}/${dateEnd}`, verbas)

      if (response.data) {
        return response.data
      } else {
        throw new Error('Erro ao atualizar o tipo de usuário')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] }) // Atualiza os dados do usuário após a alteração
    },
    onError: error => {
      console.error('Erro ao atualizar o tipo de usuário:', error)
    }
  })
}

/// DELETED

export function useDeleteCreditByID() {
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')

      const response = await api.delete(`/credits/${id}`)

      if (response.data) {
        return response.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    }
  })
}

/// FETCH

export function useFetchApuracaoByID(id_product: string) {
  const user = useCurrentUser()
  const token = useAccessToken()

  return useQuery({
    queryKey: ['apuracao', id_product],
    queryFn: async () => {
      if (!id_product) throw new Error('ID do produto ausente')
      if (!user || !token) throw new Error('Usuário ou token ausente')

      const response = await api.get(`/apuracao/${id_product}`)

      return response.data.data
    },
    enabled: Boolean(user && token && id_product)
  })
}

export function useFetchFundsByID(id_apuracao: string) {
  const user = useCurrentUser()
  const token = useAccessToken()

  return useQuery({
    queryKey: ['funds', id_apuracao],
    queryFn: async () => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      console.log(`id_apuracao`, id_apuracao)
      const response = await api.get(`/funds/${id_apuracao}`)

      if (response) {
        return response.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: Boolean(user && token && id_apuracao)
  })
}

export function useFetchCreditByID(id_apuracao: string) {
  const user = useCurrentUser()
  const token = useAccessToken()

  return useQuery({
    queryKey: ['credits', id_apuracao],
    queryFn: async () => {
      if (!user || !token) throw new Error('Usuário ou token ausente')

      const response = await api.get(`/credits/${id_apuracao}`)

      if (response.data) {
        return response.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    },
    enabled: Boolean(user && token && id_apuracao)
  })
}

//finalizar apuracao
export function useFinishApuracaoByID() {
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')

      const response = await api.post(`apuracao/finalizar/${id}`)

      if (response.data) {
        return response.data
      } else {
        throw new Error('Estrutura de resposta inesperada')
      }
    }
  })
}
